import { useEffect, useRef } from "react";
import type { Observation, TrackPoint } from "../api";
import {
  initMap,
  onMapClick,
  onMapMove,
  showRadiusCircle,
  clearRadiusCircle,
  clearMarkers,
  plotObservations,
  showTrack,
  clearTrack,
} from "../map";

interface RadiusCircle {
  lat: number;
  lon: number;
  radiusNm: number;
}

interface MapView {
  lat: number;
  lon: number;
  zoom: number;
}

interface Props {
  initialView?: MapView;
  observations: Observation[];
  trackPoints: TrackPoint[];
  trackLabel: string;
  radiusCircle: RadiusCircle | null;
  onObservationSelect: (obs: Observation) => void;
  onMapClick: (lat: number, lon: number) => void;
  onMapMove: (lat: number, lon: number, zoom: number) => void;
}

export default function MapContainer({
  initialView,
  observations,
  trackPoints,
  trackLabel,
  radiusCircle,
  onObservationSelect,
  onMapClick: handleClick,
  onMapMove: handleMove,
}: Props) {
  const initialized = useRef(false);

  // Stable refs so Leaflet callbacks never go stale
  const onSelectRef = useRef(onObservationSelect);
  const onClickRef  = useRef(handleClick);
  const onMoveRef   = useRef(handleMove);
  useEffect(() => { onSelectRef.current = onObservationSelect; }, [onObservationSelect]);
  useEffect(() => { onClickRef.current  = handleClick; },  [handleClick]);
  useEffect(() => { onMoveRef.current   = handleMove; },   [handleMove]);

  // Init Leaflet once
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    initMap(initialView);
    onMapClick((lat, lon) => onClickRef.current(lat, lon));
    onMapMove((lat, lon, zoom) => onMoveRef.current(lat, lon, zoom));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-plot observations
  useEffect(() => {
    clearMarkers();
    plotObservations(observations, (obs) => onSelectRef.current(obs));
  }, [observations]);

  // Show / clear track
  useEffect(() => {
    if (trackPoints.length > 0) {
      showTrack(trackPoints, trackLabel);
    } else {
      clearTrack();
    }
  }, [trackPoints, trackLabel]);

  // Show / clear radius circle
  useEffect(() => {
    if (radiusCircle && radiusCircle.radiusNm > 0) {
      showRadiusCircle(radiusCircle.lat, radiusCircle.lon, radiusCircle.radiusNm);
    } else {
      clearRadiusCircle();
    }
  }, [radiusCircle]);

  return <div id="map" className="w-full h-full" />;
}
